// Image import: file picker, drag-and-drop, paste-from-clipboard.
// Imported images live in an in-memory cache (see generators/image.ts).
// They are not persisted — that would bloat localStorage and the URL.

import { setImage } from '@/generators/image';
import { state, addLayer, updateLayer } from '@/core/state';
import { makeDefaultLayer } from '@/core/pipeline';
import { medianCut, rgbToHex } from './medianCut';

let counter = 0;
function nextId(): string {
  counter++;
  return `img${Date.now().toString(36)}${counter.toString(36)}`;
}

export async function importFile(file: File): Promise<boolean> {
  if (!file.type.startsWith('image/')) return false;
  return importBlob(file);
}

export async function importBlob(blob: Blob): Promise<boolean> {
  const data = await blobToImageData(blob);
  if (!data) return false;
  const id = nextId();
  setImage(id, data);
  // Extract a small palette via median-cut for auto-quantize.
  let paletteHexes: string[] | null = null;
  try {
    const colours = medianCut(data.data, 8);
    paletteHexes = colours.map(rgbToHex);
  } catch {
    paletteHexes = null;
  }
  applyToLayer(id, paletteHexes);
  return true;
}

function applyToLayer(id: string, paletteHexes: string[] | null): void {
  const s = state.peek();
  const existing = s.layers.find((l) => l.kind === 'image');
  if (existing) {
    updateLayer(existing.id, { params: { src: id } });
  } else {
    const tmpl = makeDefaultLayer('image', '_');
    addLayer({
      kind: tmpl.kind,
      params: { ...tmpl.params, src: id },
      blend: tmpl.blend,
      opacity: tmpl.opacity,
      enabled: tmpl.enabled,
      seed: tmpl.seed,
    });
  }
  // Auto-add a palette filter using extracted colours, but only if there isn't
  // already a palette filter in the stack.
  if (paletteHexes && paletteHexes.length >= 2) {
    const after = state.peek();
    const hasPalette = after.layers.some((l) => l.kind === 'palette');
    if (!hasPalette) {
      const tmpl = makeDefaultLayer('palette', '_');
      addLayer({
        kind: tmpl.kind,
        params: { palette: 'custom', custom: paletteHexes },
        blend: tmpl.blend,
        opacity: tmpl.opacity,
        enabled: tmpl.enabled,
        seed: tmpl.seed,
      });
    }
  }
}

async function blobToImageData(blob: Blob): Promise<ImageData | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = bitmap.width;
    c.height = bitmap.height;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, c.width, c.height);
  } catch {
    return null;
  }
}

/** Open a file picker for the user to select an image. */
export function openFilePicker(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.style.display = 'none';
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    if (file) await importFile(file);
    input.remove();
  });
  document.body.appendChild(input);
  input.click();
}
